import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import api from "@/api/axios";
import { API } from "@/App";
import { toast } from "sonner";

const initialState = {
    overhead_method: "EQUAL_SPLIT",
    month: "",
    monthly_operational_cost: 0,
    cumulative_operational_cost: 0,
    rows: [],
};

export function useProjectsDashboard(month) {
    const [data, setData] = useState(initialState);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const debounceTimers = useRef({}); // store debounce timers by project ID

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await api.get(`${API}/projects/dashboard`,
                // { params: { month } }
            );
            setData(response.data);
        } catch (err) {
            const errorMessage = err.response?.data?.detail || "Failed to fetch dashboard data.";
            setError(errorMessage);
            toast.error(errorMessage);
            setData(initialState);
        } finally {
            setIsLoading(false);
        }
    }, [month]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const performApiAction = useCallback(
        async (action, successMessage) => {
            try {
                await action();
                toast.success(successMessage);
                await fetchData();
            } catch (err) {
                const errorMessage = err.response?.data?.detail || "An unexpected error occurred.";
                toast.error(errorMessage);
                throw new Error(errorMessage);
            }
        },
        [fetchData]
    );

    // Filter out unchanged fields
    const getChangedFields = useCallback((projectId, patch) => {
        const project = data.rows.find((row) => row.project_id === projectId);
        if (!project) return patch; // if not found, just send patch

        console.log("data", project, projectId, patch)
        return Object.fromEntries(
            Object.entries(patch).filter(([key, value]) => project[key] !== value)
        );
    }, [data]);

    const actions = useMemo(
        () => ({
            saveOpEx: (amount) =>
                performApiAction(
                    () => api.post(`${API}/projects/opcost`, { month, amount }),
                    "Monthly operational cost saved."
                ),
            switchOverheadMethod: (currentMethod) => {
                const newMethod =
                    currentMethod === "EQUAL_SPLIT" ? "PROPORTIONAL_HOURS" : "EQUAL_SPLIT";
                return performApiAction(
                    () => api.post(`${API}/settings`, { overhead_method: newMethod }),
                    `Overhead method switched to ${newMethod.replace("_", " ")}.`
                );
            },
            updateProject: (projectId, patch) => {
                const changedFields = getChangedFields(projectId, patch);
                if (Object.keys(changedFields).length === 0) {
                    return Promise.resolve();
                }

                // Clear existing timer for this project
                if (debounceTimers.current[projectId]) {
                    clearTimeout(debounceTimers.current[projectId]);
                }

                return new Promise((resolve, reject) => {
                    debounceTimers.current[projectId] = setTimeout(async () => {
                        try {
                            await performApiAction(
                                () => api.post(`${API}/projects/${projectId}`, changedFields),
                                "Project updated successfully."
                            );
                            resolve();
                        } catch (err) {
                            reject(err);
                        }
                    }, 500); // debounce delay in ms
                });
            },
            deleteProject: (projectId) =>
                performApiAction(
                    () => api.delete(`${API}/projects/${projectId}`),
                    "Project deleted successfully."
                ),
            addProject: (projectData) =>
                performApiAction(
                    () => api.post(`${API}/projects`, projectData),
                    "Project created successfully."
                ),
            seedOriginalProjects: () =>
                performApiAction(
                    () => api.post(`${API}/projects/seed-original`),
                    "Original projects seeded."
                ),
            refresh: fetchData,
        }),
        [performApiAction, month, fetchData, getChangedFields]
    );

    return { data, isLoading, error, actions };
}
